import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { TouchableOpacity, ViewStyle, TextStyle, ActivityIndicator, AccessibilityRole } from 'react-native';

import { Text } from './text';

import { useTheme } from '@/components/theme-provider';
import { colors } from '@/styles/theme';
import type { IconName } from '@/types/icons';

export type ButtonVariant = 'primary' | 'success' | 'error' | 'warning' | 'info';

/**
 * FILLED backgrounds, chosen so the white label clears WCAG AA (4.5:1).
 *
 * Not `colors[variant]`: those are the STATUS palette, tuned to read as a tint behind dark
 * text (a chip, a badge, a row highlight). `colors.success` (#4caf50) under white measures
 * 2.8:1 — below even the 3:1 large-text floor — so a filled success button had to either
 * carry dark text, which reads as muddy on mid-green, or be illegible. Each value here is
 * the same hue two Material steps darker.
 *
 * `primary` is absent on purpose: it comes from the theme so it can change in dark mode.
 */
const FILLED_BACKGROUND: Record<Exclude<ButtonVariant, 'primary'>, string> = {
	success: '#2e7d32', // green 800 — 5.1:1, and the green the To Do Approve button already uses
	error: '#c62828', // red 800 — 5.9:1
	warning: '#a25400', // amber 900, darkened — 5.5:1 (the palette's #f57c00 is 2.7:1)
	info: '#1976d2', // blue 700 — 4.6:1
};

export interface ButtonProps {
	variant?: ButtonVariant;
	mode?: 'text' | 'outlined' | 'contained' | 'elevated';
	onPress?: () => void;
	disabled?: boolean;
	loading?: boolean;
	children: React.ReactNode;
	style?: ViewStyle;
	textStyle?: TextStyle;
	icon?: React.ReactNode | IconName;
	compact?: boolean;
	uppercase?: boolean;
	buttonColor?: string;
	textColor?: string;
	testID?: string;
	accessibilityLabel?: string;
	accessibilityRole?: AccessibilityRole;
}

export const Button: React.FC<ButtonProps> = ({
	mode = 'contained',
	onPress,
	disabled = false,
	loading = false,
	children,
	style,
	textStyle,
	icon,
	compact = false,
	uppercase = false,
	buttonColor,
	textColor,
	testID,
	accessibilityLabel,
	accessibilityRole,
	variant = 'primary',
}) => {
	const theme = useTheme();

	const getButtonStyle = (): ViewStyle => {
		const baseStyle: ViewStyle = {
			paddingHorizontal: compact ? 12 : 16,
			paddingVertical: compact ? 8 : 12,
			borderRadius: theme.roundness,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			minHeight: compact ? 32 : 40,
		};

		/**
		 * `variant` used to apply ONLY to outlined/text buttons, so a filled
		 * `variant="success"` button silently rendered in the primary blue — the prop read as
		 * a no-op at the call site with nothing to hint at it. Honoured here too now.
		 *
		 * Gated on `variant !== 'primary'` deliberately: every contained button that names no
		 * variant keeps `theme.colors.primary`, which is theme-aware (dark mode) and not
		 * necessarily the same token as `colors.primary`. So this changes the buttons that
		 * ASKED for a colour and nothing else.
		 */
		const variantBackground = variant !== 'primary' ? FILLED_BACKGROUND[variant] : theme.colors.primary;
		const backgroundColor = buttonColor
			|| (mode === 'contained' || mode === 'elevated' ? variantBackground : 'transparent');

		switch (mode) {
			case 'contained':
				return {
					...baseStyle,
					backgroundColor: disabled ? theme.colors.outline : backgroundColor,
				};
			case 'outlined':
				return {
					...baseStyle,
					backgroundColor: 'white',
					borderWidth: 1,
					borderColor: disabled ? theme.colors.outline : (buttonColor || colors[variant || 'primary']),
				};
			case 'elevated':
				return {
					...baseStyle,
					backgroundColor: disabled ? theme.colors.outline : backgroundColor,
					shadowColor: '#000',
					shadowOffset: { width: 0, height: 2 },
					shadowOpacity: 0.25,
					shadowRadius: 3.84,
					elevation: 5,
				};
			case 'text':
			default:
				return {
					...baseStyle,
					backgroundColor: 'transparent',
				};
		}
	};

	const getTextStyle = (): TextStyle => {
		const baseTextStyle: TextStyle = {
			fontSize: compact ? 12 : 14,
			fontWeight: 'bold',
			...(uppercase && { textTransform: 'uppercase' }),
		};

		switch (mode) {
			case 'contained':
			case 'elevated':
				return {
					...baseTextStyle,
					color: textColor || 'white',
				};
			case 'outlined':
			case 'text':
			default:
				return {
					...baseTextStyle,
					color: textColor || (disabled ? theme.colors.outline : colors[`${variant}Dark`]),
				};
		}
	};

	return (
		<TouchableOpacity
			style={[getButtonStyle(), style]}
			onPress={onPress}
			disabled={disabled || loading}
			activeOpacity={0.7}
			testID={testID}
			accessibilityLabel={accessibilityLabel}
			accessibilityRole={accessibilityRole || 'button' as AccessibilityRole}
			accessibilityState={{ disabled: disabled || loading }}
		>
			{loading ? (
				<ActivityIndicator
					size="small"
					color={mode === 'contained' || mode === 'elevated' ? theme.colors.onPrimary : theme.colors.primary}
				/>
			) : (
				<>
					{icon && (
						typeof icon === 'string' ? (
							<Ionicons
								name={icon as IconName}
								size={16}
								color={mode === 'contained' || mode === 'elevated' ? theme.colors.onPrimary : theme.colors.primary}
								style={{ marginRight: 8 }}
							/>
						) : (
							<>{icon}</>
						)
					)}
					<Text style={[getTextStyle(), textStyle]}>
						{children}
					</Text>
				</>
			)}
		</TouchableOpacity>
	);
};
